import React, { useState } from "react";
import { formwizApi, getConfigCached } from "./formwizApi";
import { eventBus } from "../../utils/eventBus";
import { practiceFullName } from "../../config/practice";
import LinkBox, { deliveryMessage } from "./LinkBox";
import { canCancelRequest, canOpenOnTablet, canResendRequest, errInfo } from "./staffUtils";

// Emits the kiosk hand-off for an open request: the patient page's overlay
// (FormKioskHost, mounted by the app) takes over this device.
export async function openRequestOnTablet(requestId, patientName) {
  const ks = await formwizApi.kioskSession(requestId);
  let practiceName = practiceFullName();
  try {
    const cfg = await getConfigCached();
    if (cfg && cfg.practice && cfg.practice.practice_display_name) practiceName = cfg.practice.practice_display_name;
  } catch (_) {
    /* the profile name will do */
  }
  eventBus.emit("formwiz:kiosk", { session: ks && ks.session, requestId, patientName: patientName || "", practiceName });
  return ks;
}

/*
 * Resend / Cancel / Open on this tablet for one request (chart panel and
 * submission review). Resend rotates the link (the old one stops working),
 * unlocks a DOB lock and extends the expiry; the new link is shown once.
 */
export default function RequestActions({ request, patientName, onChanged, size = "sm" }) {
  const [busy, setBusy] = useState("");
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [outcome, setOutcome] = useState(null);
  const [error, setError] = useState("");
  if (!request) return null;
  const btn = `fws-btn${size === "sm" ? " fws-btn-sm" : ""}`;
  const showResend = canResendRequest(request);
  const showCancel = canCancelRequest(request);
  const showTablet = canOpenOnTablet(request);
  if (!showResend && !showCancel && !showTablet && !outcome) return null;

  const run = async (kind, fn) => {
    setBusy(kind);
    setError("");
    try {
      await fn();
    } catch (e) {
      const info = errInfo(e);
      if (info.status === 429) setError("Daily send limit reached for this patient. Try Copy link or Tablet tomorrow.");
      else if (info.status === 409) setError(info.message || "This request is already closed.");
      else if (info.status === 403) setError("You don't have access to this patient.");
      else setError(info.message || "That didn't work. Try again.");
    } finally {
      setBusy("");
    }
  };

  const resend = () => run("resend", async () => {
    const data = await formwizApi.resend(request.id, {});
    setOutcome({ ...data, channel: (data && data.delivery && data.delivery.channel) || request.channel });
    eventBus.emit("formwiz:requests-changed", { patientid: request.patientid });
    onChanged && onChanged(data && data.request);
  });
  const cancel = () => run("cancel", async () => {
    const data = await formwizApi.cancel(request.id);
    setConfirmCancel(false);
    eventBus.emit("formwiz:requests-changed", { patientid: request.patientid });
    onChanged && onChanged(data);
  });
  const tablet = () => run("tablet", async () => {
    await openRequestOnTablet(request.id, patientName);
  });

  const msg = outcome ? deliveryMessage(outcome.channel, outcome.delivery) : null;
  return (
    <div className="fws-stack" style={{ marginTop: 8 }}>
      <div className="fws-row">
        {showTablet && (
          <button type="button" className={`${btn} fws-btn-primary`} disabled={!!busy} onClick={tablet}>
            {busy === "tablet" ? "Opening…" : "Open on this tablet"}
          </button>
        )}
        {showResend && (
          <button type="button" className={btn} disabled={!!busy} onClick={resend}>
            {busy === "resend" ? "Resending…" : "Resend"}
          </button>
        )}
        {showCancel && !confirmCancel && (
          <button type="button" className={`${btn} fws-btn-danger`} disabled={!!busy} onClick={() => setConfirmCancel(true)}>
            Cancel request
          </button>
        )}
        {showCancel && confirmCancel && (
          <span className="fws-row" role="group" aria-label="Confirm cancel">
            <span className="fws-small">Cancel? The link stops working.</span>
            <button type="button" className={`${btn} fws-btn-danger`} disabled={!!busy} onClick={cancel}>
              {busy === "cancel" ? "Cancelling…" : "Yes, cancel"}
            </button>
            <button type="button" className={`${btn} fws-btn-ghost`} disabled={!!busy} onClick={() => setConfirmCancel(false)}>
              Keep
            </button>
          </span>
        )}
      </div>
      {error && <div className="fws-alert fws-alert-error" role="alert">{error}</div>}
      {msg && (
        <div className="fws-stack">
          <div className={`fws-alert fws-alert-${msg.tone}`} role="status">{msg.text}</div>
          {outcome.link && <LinkBox link={outcome.link} />}
        </div>
      )}
    </div>
  );
}
